/**
 * BatteryStartModal
 *
 * Patient selection modal shown before starting a battery session.
 * The clinician picks a patient, then we create the BatterySession in the DB
 * and navigate to BatteryRunner.
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TestBattery, Patient } from '../../types';
import { getPatients } from '../../services/patientService';
import { createBatterySession } from '../../services/batteryService';
import { toast } from 'sonner';

interface Props {
  battery: TestBattery;
  clinicianId: string;
  onStart: (sessionId: string) => void;
  onClose: () => void;
}

export default function BatteryStartModal({ battery, clinicianId, onStart, onClose }: Props) {
  const { t } = useTranslation('app');
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    getPatients(false).then((ps) => {
      setPatients(ps);
      setLoading(false);
    });
  }, []);

  const filtered = patients.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleStart = async () => {
    if (!selectedPatientId || starting) return;
    setStarting(true);

    const session = await createBatterySession({
      batteryId:   battery.isTemplate ? undefined : battery.id,
      batteryName: battery.name,
      patientId:   selectedPatientId,
      clinicianId,
      testIds:     battery.testIds,
    });

    if (!session) {
      toast.error(t('batteryStart.errorSession'));
      setStarting(false);
      return;
    }

    onStart(session.id);
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col max-h-[90vh]">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
            <div>
              <h2 className="text-base font-bold text-slate-900">{t('batteryStart.title')}</h2>
              <p className="text-xs text-slate-500 mt-0.5 truncate max-w-[280px]">{battery.name}</p>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Battery info */}
          <div className="px-6 pt-4 pb-2">
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 mb-4">
              <p className="text-xs font-bold text-blue-700 mb-1">
                {t('batteryStart.testCount', { count: battery.testIds.length })}
              </p>
              <p className="text-xs text-blue-600">
                {battery.description || t('batteryStart.sequential')}
              </p>
            </div>

            <label className="block text-sm font-medium text-slate-700 mb-2">{t('batteryStart.patientLabel')}</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('batteryStart.patientPlaceholder')}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
              autoFocus
            />
          </div>

          {/* Patient list */}
          <div className="flex-1 overflow-y-auto px-6 pb-4">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
              </div>
            ) : filtered.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-6">
                {patients.length === 0 ? t('batteryStart.noPatients') : t('batteryStart.noResults')}
              </p>
            ) : (
              <div className="space-y-1">
                {filtered.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setSelectedPatientId(p.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                      selectedPatientId === p.id
                        ? 'bg-blue-600 text-white'
                        : 'hover:bg-slate-50 text-slate-800'
                    }`}
                  >
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                      selectedPatientId === p.id ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
                    }`}>
                      {p.name.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-sm font-medium truncate">{p.name}</span>
                    {selectedPatientId === p.id && (
                      <svg className="w-4 h-4 ml-auto flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between gap-3">
            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors">
              {t('batteryStart.cancel')}
            </button>
            <button
              onClick={handleStart}
              disabled={!selectedPatientId || starting}
              className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {starting ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  {t('batteryStart.starting')}
                </>
              ) : (
                t('batteryStart.start')
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
